import { create } from "zustand";
import {
  integrationsApi,
  type IntegrationStatus,
  type ActivitySummary,
} from "../lib/api";

interface IntegrationState {
  provider: string;
  status: IntegrationStatus;
  lastSyncAt: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  needsReconnect: boolean;
}

interface IntegrationsState {
  // State per provider
  integrations: Record<string, IntegrationState>;

  // Activities (currently only from one provider at a time)
  activities: ActivitySummary[];
  activitiesLoading: boolean;
  activitiesError: string | null;

  // Modal state
  connectModalOpen: boolean;
  connectModalProvider: string | null;

  // Actions
  fetchStatus: (provider: string) => Promise<void>;
  connect: (provider: string, email: string, password: string) => Promise<boolean>;
  disconnect: (provider: string) => Promise<void>;
  fetchActivities: (provider: string, options?: { limit?: number }) => Promise<void>;
  openConnectModal: (provider: string) => void;
  closeConnectModal: () => void;
  initiateOAuth: (provider: string) => Promise<void>;
  reset: () => void;
}

const initialIntegrationState: IntegrationState = {
  provider: "",
  status: "disconnected",
  lastSyncAt: null,
  errorMessage: null,
  isLoading: false,
  needsReconnect: false,
};

export const useIntegrationsStore = create<IntegrationsState>((set, _get) => ({
  integrations: {},
  activities: [],
  activitiesLoading: false,
  activitiesError: null,
  connectModalOpen: false,
  connectModalProvider: null,

  fetchStatus: async (provider: string) => {
    set((state) => ({
      integrations: {
        ...state.integrations,
        [provider]: {
          ...initialIntegrationState,
          ...state.integrations[provider],
          provider,
          isLoading: true,
        },
      },
    }));

    try {
      const data = await integrationsApi.status(provider);
      // Check if error message suggests reconnection is needed
      const needsReconnect = data.status === "error" && Boolean(
        data.errorMessage?.includes("reconnect") || 
        data.errorMessage?.includes("expired") ||
        data.errorMessage?.includes("Session")
      );
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            provider: data.provider,
            status: data.status,
            lastSyncAt: data.lastSyncAt,
            errorMessage: data.errorMessage,
            isLoading: false,
            needsReconnect,
          },
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch status";
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            ...state.integrations[provider],
            provider,
            isLoading: false,
            errorMessage: message,
          },
        },
      }));
    }
  },

  connect: async (provider: string, email: string, password: string) => {
    set((state) => ({
      integrations: {
        ...state.integrations,
        [provider]: {
          ...initialIntegrationState,
          ...state.integrations[provider],
          provider,
          isLoading: true,
          errorMessage: null,
        },
      },
    }));

    try {
      const data = await integrationsApi.connect(provider, email, password);
      if (data.success) {
        set((state) => ({
          integrations: {
            ...state.integrations,
            [provider]: {
              provider,
              status: data.status,
              lastSyncAt: new Date().toISOString(),
              errorMessage: null,
              isLoading: false,
              needsReconnect: false,
            },
          },
          connectModalOpen: false,
          connectModalProvider: null,
        }));
        return true;
      } else {
        set((state) => ({
          integrations: {
            ...state.integrations,
            [provider]: {
              ...state.integrations[provider],
              provider,
              isLoading: false,
              errorMessage: data.error || "Connection failed",
            },
          },
        }));
        return false;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Connection failed";
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            ...state.integrations[provider],
            provider,
            isLoading: false,
            errorMessage: message,
          },
        },
      }));
      return false;
    }
  },

  disconnect: async (provider: string) => {
    set((state) => ({
      integrations: {
        ...state.integrations,
        [provider]: {
          ...state.integrations[provider],
          provider,
          isLoading: true,
        },
      },
    }));

    try {
      await integrationsApi.disconnect(provider);
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            provider,
            status: "disconnected",
            lastSyncAt: null,
            errorMessage: null,
            isLoading: false,
            needsReconnect: false,
          },
        },
        activities: [],
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Disconnect failed";
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            ...state.integrations[provider],
            provider,
            isLoading: false,
            errorMessage: message,
          },
        },
      }));
    }
  },

  fetchActivities: async (provider: string, options?: { limit?: number }) => {
    set({ activitiesLoading: true, activitiesError: null });

    try {
      const data = await integrationsApi.activities(provider, options);
      set({
        activities: data.activities,
        activitiesLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch activities";
      set({
        activitiesLoading: false,
        activitiesError: message,
      });
    }
  },

  openConnectModal: (provider: string) => {
    set({ connectModalOpen: true, connectModalProvider: provider });
  },

  closeConnectModal: () => {
    set({ connectModalOpen: false, connectModalProvider: null });
  },

  initiateOAuth: async (provider: string) => {
    // Set loading state
    set((state) => ({
      integrations: {
        ...state.integrations,
        [provider]: {
          ...initialIntegrationState,
          ...state.integrations[provider],
          provider,
          isLoading: true,
          errorMessage: null,
        },
      },
    }));

    try {
      // Get OAuth URL from authenticated endpoint
      const { url } = await integrationsApi.oauthUrl(provider);
      // Redirect to OAuth provider
      window.location.href = url;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to start OAuth";
      set((state) => ({
        integrations: {
          ...state.integrations,
          [provider]: {
            ...state.integrations[provider],
            provider,
            status: "error",
            isLoading: false,
            errorMessage: message,
          },
        },
      }));
    }
  },

  reset: () => {
    set({
      integrations: {},
      activities: [],
      activitiesLoading: false,
      activitiesError: null,
      connectModalOpen: false,
      connectModalProvider: null,
    });
  },
}));
